// src/products/capRaise/state/capRaiseStore.js
import { create } from 'zustand';
import { supabase } from '../../../config';
import RoleService from '../../../services/roleService';
import { useGlobalStore } from '../../../state/globalStore';

export const useCapRaiseStore = create((set, get) => ({
  // --- STATE ---
  companies: [],
  selectedCompany: null,
  activeRound: null,
  loading: true,
  messages: [],
  userRole: null, // Round-specific role
  canCreate: false, // Product-level permission

  // --- ACTIONS ---
  addMessage: (text, type = 'info') => {
    const id = Date.now();
    set(state => ({ messages: [...state.messages, { id, text, type }] }));
    setTimeout(() => {
      set(state => ({ messages: state.messages.filter(msg => msg.id !== id) }));
    }, 5000);
  },

  checkCanCreate: async () => {
    const user = useGlobalStore.getState().user;
    if (!user?.id) return;
    try {
      const canCreateRounds = await RoleService.canCreateInProduct(user.id, RoleService.PRODUCTS.CAPRAISE);
      set({ canCreate: canCreateRounds });
    } catch (error) {
      console.error('Error checking create permission:', error);
      set({ canCreate: false });
    }
  },

  checkRoundRole: async (roundId) => {
    const user = useGlobalStore.getState().user;
    if (!user?.id || !roundId) {
      set({ userRole: null });
      return;
    }
    try {
      const role = await RoleService.getCapRaiseRole(user.id, roundId);
      set({ userRole: role || 'viewer' });
    } catch (error) {
      console.error('Error checking round role:', error);
      set({ userRole: 'viewer' }); // Default to viewer on error
    }
  },

  loadCompanies: async () => {
    const user = useGlobalStore.getState().user;
    if (!user?.id) return;

    try {
      const { data: companiesData, error: companiesError } = await supabase
        .from('equity_companies')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (companiesError) throw companiesError;

      // Store companies in state
      set({ companies: companiesData || [] });

      if (companiesData && companiesData.length > 0 && !get().selectedCompany) {
        get().handleCompanyChange(companiesData[0]);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
      get().addMessage('Failed to load companies', 'error');
    } finally {
      set({ loading: false });
    }
  },

  loadRounds: async () => {
    const { selectedCompany } = get();
    if (!selectedCompany) return;

    try {
      const { error: roundsError } = await supabase
        .from('capraise_rounds')
        .select('*')
        .eq('company_id', selectedCompany.id)
        .order('created_at', { ascending: false });

      if (roundsError) throw roundsError;

      // Don't auto-select rounds - users should explicitly select from dashboard
      // This prevents auto-selection when switching companies in a blank form
    } catch (error) {
      console.error('Error loading rounds:', error);
      get().addMessage('Failed to load rounds', 'error');
    }
  },

  handleRoundCreated: async (newRound) => {
    const user = useGlobalStore.getState().user;
    if (!get().canCreate) {
      get().addMessage('You need Creator access to create rounds', 'error');
      return;
    }
    try {
      await RoleService.assignCapRaiseRole(user.id, newRound.id, RoleService.ROLES.CREATOR);
      await get().loadRounds();
      set({ activeRound: newRound });
    } catch (error) {
      console.error('Error setting up round roles:', error);
      get().addMessage('Round created but role assignment failed', 'error');
    }
  },

  handleCompanyChange: (company) => {
    set({ selectedCompany: company, activeRound: null });
    get().loadRounds();
  },

  handleRoundChange: (round) => {
    set({ activeRound: round });
  },
}));
